import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Eye, Save, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import Navigation from "@/components/Navigation";
import AdminLogin from "@/components/AdminLogin";
import type { BlogPost, InsertBlogPost, UpdateBlogPost } from "../types/schema";

interface BlogFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  featuredImage: string;
  tags: string;
  published: boolean;
}

export default function AdminBlog() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<BlogFormData>({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    author: "UnicornClub Team",
    featuredImage: "",
    tags: "",
    published: false,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (token) {
          await apiRequest("/api/admin/check-auth", "GET");
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('adminToken');
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, []);

  // Always call hooks before any conditional returns
  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/admin/blog/posts"],
    enabled: isAuthenticated && authChecked,
  });

  const createMutation = useMutation({
    mutationFn: (data: InsertBlogPost) => apiRequest("/api/admin/blog/posts", "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog/posts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/blog/posts"] });
      resetForm();
      toast({ title: "Success", description: "Blog post created successfully" });
    },
    onError: (error: any) => {
      console.error("Blog creation error:", error);
      toast({ title: "Error", description: "Failed to create blog post. Please try again.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBlogPost }) =>
      apiRequest(`/api/admin/blog/posts/${id}`, "PUT", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog/posts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/blog/posts"] });
      resetForm();
      toast({ title: "Success", description: "Blog post updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update blog post", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/admin/blog/posts/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog/posts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/blog/posts"] });
      toast({ title: "Success", description: "Blog post deleted successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete blog post", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      author: "UnicornClub Team",
      featuredImage: "",
      tags: "",
      published: false,
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const slug = formData.slug || formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const tags = formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [];
    
    const postData = {
      ...formData,
      slug,
      tags,
      publishedAt: formData.published ? new Date() : null,
    };

    if (editingPost) {
      updateMutation.mutate({ id: editingPost.id, data: postData });
    } else {
      createMutation.mutate(postData);
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      featuredImage: post.featuredImage || "",
      tags: post.tags?.join(', ') || "",
      published: post.published,
    });
    setShowForm(true);
  };

  const generateSlug = (title: string) => {
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return "Not published";
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const handleLogout = async () => {
    try {
      await apiRequest("/api/admin/logout", "POST");
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
      });
    } catch (error) {
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
      });
    }
  };

  // Show login form if authentication hasn't been checked or user is not authenticated
  if (!authChecked) {
    return <div className="min-h-screen bg-gradient-to-br from-charcoal via-dark-slate to-charcoal flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>;
  }

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-dark-slate to-charcoal text-white">
      <Navigation />
      
      <main className="pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-neon-pink to-cyber-purple bg-clip-text text-transparent">
              Blog Admin
            </h1>
            <div className="flex gap-3">
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              <Button
                onClick={() => setShowForm(!showForm)}
                className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
          </div>

          {showForm && (
            <Card className="bg-dark-slate/50 border-gray-700 mb-8">
              <CardHeader>
                <CardTitle className="text-white">
                  {editingPost ? "Edit Post" : "Create New Post"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, title: e.target.value }));
                          if (!editingPost) generateSlug(e.target.value);
                        }}
                        className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white">Slug *</Label>
                      <Input
                        value={formData.slug}
                        onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                        className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">Excerpt *</Label>
                    <Textarea
                      value={formData.excerpt}
                      onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                      className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-white">Content *</Label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                      rows={10}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">Author</Label>
                      <Input
                        value={formData.author}
                        onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                        className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Featured Image URL</Label>
                      <Input
                        value={formData.featuredImage}
                        onChange={(e) => setFormData(prev => ({ ...prev, featuredImage: e.target.value }))}
                        className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">Tags (comma separated)</Label>
                    <Input
                      value={formData.tags}
                      onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                      className="bg-white border-gray-600 text-black placeholder:text-gray-500"
                      placeholder="react, typescript, web development"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.published}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
                    />
                    <Label className="text-white">Published</Label>
                  </div>

                  <div className="flex space-x-4">
                    <Button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingPost ? "Update" : "Create"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={resetForm}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {isLoading ? (
            <div className="grid gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="bg-dark-slate/50 border-gray-700 animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid gap-4">
              {posts?.map((post) => (
                <Card key={post.id} className="bg-dark-slate/50 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-semibold text-white">{post.title}</h3>
                          <Badge variant={post.published ? "default" : "secondary"}>
                            {post.published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        <p className="text-gray-400 mb-2">{post.excerpt}</p>
                        <div className="text-sm text-gray-500">
                          <span>By {post.author}</span>
                          <span className="mx-2">•</span>
                          <span>{formatDate(post.publishedAt)}</span>
                          {post.tags && post.tags.length > 0 && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{post.tags.join(', ')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {post.published && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                            className="text-gray-500 hover:text-blue-400 hover:bg-blue-400/10 transition-all duration-200"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(post)}
                          className="text-gray-500 hover:text-green-400 hover:bg-green-400/10 transition-all duration-200"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteMutation.mutate(post.id)}
                          disabled={deleteMutation.isPending}
                          className="text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {posts?.length === 0 && (
                <div className="text-center py-16">
                  <h3 className="text-2xl font-bold text-gray-400 mb-4">No blog posts yet</h3>
                  <p className="text-gray-500">Create your first blog post to get started!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}